// /mi/search?q=...&on_lead_list=true&entity_type=individual,llc
//
// Dedicated search page. URL-backed state so a search is a shareable
// link. Filter pills toggle (on lead list / individuals only) inline.
// Each result reuses SearchResultRow — same chips, same indicators
// as a lead-list row.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { partyApi, type PartySearchResponse } from '../services/partyApi';
import { SearchResultRow } from '../components/SearchResultRow';

const ROUTE_LIMIT = 50;

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q              = searchParams.get('q') ?? '';
  const onLeadList     = searchParams.get('on_lead_list') === 'true';
  const individualsOnly = searchParams.get('entity_type') === 'individual';

  const [draft, setDraft] = useState(q);
  const [data,  setData]  = useState<PartySearchResponse | null>(null);
  const [err,   setErr]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  // Sync draft → URL on Enter / blur. Live-typing doesn't fire the
  // search until the operator commits, so the dedicated route is a
  // deliberate "I want to see all the results" surface vs the
  // palette's "type-as-you-go" feel.
  function commit() {
    const next = new URLSearchParams(searchParams);
    if (draft.trim()) next.set('q', draft.trim());
    else              next.delete('q');
    setSearchParams(next, { replace: false });
  }

  function toggleParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (next.get(key) === value) next.delete(key);
    else                          next.set(key, value);
    setSearchParams(next, { replace: false });
  }

  // Refetch whenever the URL params change.
  useEffect(() => {
    if (!q.trim()) { setData(null); setErr(null); return; }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    partyApi.search(q.trim(), {
      limit: ROUTE_LIMIT,
      onLeadList,
      entityTypes: individualsOnly ? ['individual'] : undefined,
    })
      .then(r => {
        if (reqId !== reqIdRef.current) return;
        setData(r);
      })
      .catch(e => {
        if (reqId !== reqIdRef.current) return;
        setErr(e?.message ?? 'Search failed');
      })
      .finally(() => {
        if (reqId !== reqIdRef.current) return;
        setLoading(false);
      });
  }, [q, onLeadList, individualsOnly]);

  // Keep the draft in sync if the URL changed (e.g. browser back).
  useEffect(() => { setDraft(q); }, [q]);

  const resultCount = data?.results.length ?? 0;
  const hitCap = data?.hit_cap ?? false;

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace · search</div>
        <h1 className="font-display text-[28px] leading-tight">Party search</h1>
        <div className="text-[12px] text-text-dim mt-1.5 font-mono">
          Fuzzy match across names + aliases. Digit-heavy queries also match phone numbers.
        </div>
      </header>

      <div className="mt-5 flex items-center gap-2">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); }}
          onBlur={commit}
          placeholder='Try "Smith John" or "303-555" or a partial name'
          autoFocus
          className="flex-1 bg-bg-input border border-border rounded px-3 py-2 text-[14px] outline-none focus:border-accent"
        />
        <button onClick={commit}
          className="px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
          Search
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => toggleParam('on_lead_list', 'true')}
          className={
            'px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (onLeadList
              ? 'border-amber/40 bg-amber/10 text-amber'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          ● On lead list
        </button>
        <button onClick={() => toggleParam('entity_type', 'individual')}
          className={
            'px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (individualsOnly
              ? 'border-green/40 bg-green/10 text-green'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          ◆ Individuals only
        </button>
        <div className="ml-auto text-[11px] font-mono text-text-faint">
          {q.trim()
            ? loading ? 'searching…' : `${resultCount}${hitCap ? '+' : ''} ${resultCount === 1 ? 'result' : 'results'}${hitCap ? ' (cap; refine to see more)' : ''}`
            : 'type a query to search'}
        </div>
      </div>

      {err && (
        <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      )}

      <div className="mt-5 space-y-1">
        {q.trim() && !loading && resultCount === 0 && !err && (
          <div className="bg-bg-card-soft border border-border rounded px-6 py-12 text-center">
            <div className="text-[13px] text-text-dim">No matches.</div>
            <div className="text-[11px] text-text-faint font-mono mt-2">
              Try fewer characters, drop a filter, or check spelling.
            </div>
          </div>
        )}
        {data?.results.map(r => (
          <SearchResultRow key={r.party_id} result={r} />
        ))}
      </div>
    </div>
  );
}
